import { useEffect, useRef, useState } from "react";
import { ImagePlus, Loader2, Send, TestTube2, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatNewsDate } from "./News";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/[0.05] px-4 py-3 text-sm text-white placeholder:text-white/35 outline-none transition-all duration-300 focus:border-primary focus:bg-white/[0.08] focus:ring-2 focus:ring-primary/30";

export const NewsletterComposer = ({ subsCount }) => {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [sending, setSending] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const fileRef = useRef(null);

  const loadCampaigns = () =>
    axios
      .get(`${API}/admin/newsletter/campaigns`, { withCredentials: true })
      .then((res) => setCampaigns(res.data.items || []))
      .catch(() => {});

  useEffect(() => {
    loadCampaigns();
  }, []);

  const send = async (test) => {
    if (!subject.trim() || !content.trim()) {
      toast.error("Compila oggetto e testo della newsletter.");
      return;
    }
    if (!test && !window.confirm(`Inviare la newsletter a tutti i ${subsCount} iscritti?`)) return;
    setSending(test ? "test" : "all");
    try {
      let image_path = null;
      if (imageFile) {
        const fd = new FormData();
        fd.append("file", imageFile);
        const up = await axios.post(`${API}/admin/news/upload`, fd, { withCredentials: true });
        image_path = up.data.image_path;
      }
      const res = await axios.post(
        `${API}/admin/newsletter/send`,
        { subject, content, image_path, origin_url: window.location.origin, test },
        { withCredentials: true }
      );
      if (test) {
        toast.success("Email di prova inviata alla tua casella!");
      } else {
        toast.success(`Newsletter inviata a ${res.data.sent} iscritti${res.data.failed ? ` (${res.data.failed} falliti)` : ""}!`);
        setSubject("");
        setContent("");
        setImageFile(null);
        if (fileRef.current) fileRef.current.value = "";
        loadCampaigns();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invio non riuscito. Riprova.");
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="mt-12 grid gap-10 lg:grid-cols-[1.2fr_1fr]" data-testid="newsletter-composer">
      <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-7">
        <h2 className="font-serif text-2xl text-white">Scrivi la newsletter</h2>
        <p className="mt-2 text-sm text-white/50">
          Verrà spedita così com'è a tutti gli iscritti. Prova prima l'invio a te stesso.
        </p>
        <div className="mt-6 flex flex-col gap-4">
          <input
            data-testid="newsletter-subject-input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Oggetto (es. Notizie di ottobre dal Madagascar)"
            maxLength={200}
            className={inputCls}
          />
          <textarea
            data-testid="newsletter-content-input"
            rows={8}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Testo della newsletter (vai a capo per creare paragrafi)"
            className={`${inputCls} resize-y`}
          />
          <div className="rounded-2xl border border-dashed border-white/20 p-3">
            {imageFile ? (
              <div className="relative">
                <img src={URL.createObjectURL(imageFile)} alt="Anteprima" className="max-h-44 w-full rounded-xl object-cover" />
                <button
                  type="button"
                  data-testid="newsletter-image-remove"
                  onClick={() => {
                    setImageFile(null);
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                  className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-black/60 text-white hover:bg-black/80"
                >
                  <X size={15} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="newsletter-image-pick"
                onClick={() => fileRef.current?.click()}
                className="flex w-full items-center justify-center gap-2 py-4 text-sm text-white/50 transition-colors hover:text-white"
              >
                <ImagePlus size={18} />
                Foto in testa alla email (opzionale)
              </button>
            )}
            <input
              ref={fileRef}
              data-testid="newsletter-image-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => setImageFile(e.target.files?.[0] || null)}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              data-testid="newsletter-send-test"
              onClick={() => send(true)}
              disabled={!!sending}
              className="flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-white/80 transition-colors hover:border-white/50 hover:text-white disabled:opacity-60"
            >
              {sending === "test" ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Invia prova a me
            </button>
            <button
              data-testid="newsletter-send-all"
              onClick={() => send(false)}
              disabled={!!sending}
              className="flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-white transition-all hover:bg-[#e0562a] disabled:opacity-60"
            >
              {sending === "all" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Invia a tutti ({subsCount})
            </button>
          </div>
        </div>
      </div>

      <div>
        <h2 className="font-serif text-2xl text-white">Invii precedenti</h2>
        <div className="mt-5 flex flex-col gap-3" data-testid="newsletter-campaigns-list">
          {campaigns.map((c) => (
            <div key={c.campaign_id} className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <p className="font-serif text-lg text-white">{c.subject}</p>
              <p className="mt-1 text-[11px] uppercase tracking-[0.15em] text-white/40">
                {formatNewsDate(c.sent_at, "it")} · inviata a {c.sent_count} iscritti
                {c.failed_count > 0 && <span className="text-red-300"> · {c.failed_count} falliti</span>}
              </p>
            </div>
          ))}
          {campaigns.length === 0 && (
            <p className="text-sm text-white/40">Nessuna newsletter inviata finora.</p>
          )}
        </div>
      </div>
    </div>
  );
};
